import {
  Badge,
  Card,
  ExpandableMenuListItem,
  Icon,
  MenuListItem,
  MobileDropdown,
  Text
} from '@albert-io/atomic';
import {List, OrderedMap} from 'immutable';
import React from 'react';
import {Link, withRouter} from 'react-router';

import {SUBJECT_BADGE_TYPES} from 'client/constants';
import {AdaptiveSubject} from 'client/SubjectLibrary/AdaptiveSubjects.types';
import {ALL_SUBJECTS_STRING} from 'client/SubjectLibrary/subjectLibraryConstants';

import {ADAPTIVE_LABEL} from '../constants';
import {getOverriddenDomainString, getOverriddenGroupString} from '../stringOverrideHelpers';
import {SubjectMenuSectionItem} from '../SubjectMenuSectionItem/SubjectMenuSectionItem';
import './sidebar-menu.scss';

interface Props {
  activePath: string[];
  additionalSidebarMenuItems?: OrderedMap<string, Record<string, any>>;
  group: string;
  makeItemHref?: (category: string, item: string) => string;
  menuItems: OrderedMap<string, List<string>>;
  onItemClick?: (category: string, item: string) => void;
  router: any;
  subgroup: string;
  adaptiveSubjects?: AdaptiveSubject[];
  canSeeFreeLicensedBadges?: boolean;
}

function SidebarMenu({
  activePath,
  additionalSidebarMenuItems = OrderedMap(),
  group,
  makeItemHref,
  menuItems,
  onItemClick = () => {},
  router,
  subgroup,
  adaptiveSubjects = [],
  canSeeFreeLicensedBadges = false
}: Props) {
  function getCombinedAdditionalSidebarMenuItems() {
    return additionalSidebarMenuItems
      .entrySeq()
      .concat(
        List([
          [
            ALL_SUBJECTS_STRING,
            {
              leftIcon: 'books',
              rightContent: canSeeFreeLicensedBadges ? (
                <Badge
                  roundness='square'
                  status='info'
                  emphasis='subtle'
                  size='small'
                  className='a-badge--status-info--default'
                >
                  Licensed
                </Badge>
              ) : null
            }
          ]
        ])
      )
      .toList();
  }
  /**
   * Since 'activePath' was created to traverse 'subjectMenuData' and not handle
   * outside categories such as 'Discover' and 'All Subjects' we need to do extra manual checks to
   * apply the selected state to any domains that are not in the menuData.
   *
   * 'All Subjects' exists in all instances of the sidebar and requires a special check due to being hardcoded
   *
   * @param  {string}  category
   * @param  {string}  item
   * @returns {boolean}
   */
  function isItemActive(category: string, item: string) {
    const [activeCategory, activeItem] = activePath;

    if (
      (!activeCategory && category === ALL_SUBJECTS_STRING) ||
      activeCategory === category.toLowerCase()
    ) {
      return true;
    }

    const isActiveCategory = activeCategory === category;
    const isActiveItem = activeItem ? activeItem === item : item === 'All';

    return isActiveCategory && isActiveItem;
  }

  function renderAdaptiveSubjectMenuItems() {
    return (
      <div className='flex flex-col px-x2'>
        {adaptiveSubjects.map((subject) => {
          const badgeText = SUBJECT_BADGE_TYPES[subject.badge as keyof typeof SUBJECT_BADGE_TYPES];
          return (
            <MenuListItem
              key={subject.id}
              indentLevel='1'
              as={Link}
              to={`/adaptive-subjects/${subject.urlSlug}`}
              className='justify-start px-x2 py-x1-half'
            >
              <div className='flex w-full items-center justify-between gap-x2'>
                <Text size='m'>{subject.name}</Text>
                {badgeText && (
                  <Badge roundness='square' status='special' emphasis='default' size='small'>
                    {badgeText}
                  </Badge>
                )}
              </div>
            </MenuListItem>
          );
        })}
      </div>
    );
  }

  function renderAdditionalSidebarMenuItems() {
    return (
      <div className='u-gap_space-x3 flex flex-col'>
        {getCombinedAdditionalSidebarMenuItems().map((item) => {
          // @ts-ignore this is a valid tuple of an object's entries
          const [category, props] = item;

          // Assumes adaptiveSubjects is already sorted/filtered before being passed in as a prop.
          if (category === ADAPTIVE_LABEL) {
            props.children = renderAdaptiveSubjectMenuItems();
          }
          return (
            <SubjectMenuSectionItem
              sectionProps={props}
              to={makeItemHref?.(category, 'All')}
              onClick={() => onItemClick(category, 'All')}
              isSelected={isItemActive(category, 'All')}
              text={getOverriddenDomainString(category) ?? category}
              data-testid={category === ADAPTIVE_LABEL ? 'adaptive-practice-btn' : undefined}
            />
          );
        })}
      </div>
    );
  }

  const renderCategories = (category: string, items: List<string>) => {
    const isCategoryActive = activePath[0] === category;

    return (
      <li className='cl-sidebar-menu__category' key={category}>
        <ExpandableMenuListItem
          categoryTextProps={{color: 'inherit', isBold: false}}
          defaultOnState={isCategoryActive}
          expandOnMenuItemClick
          isCategoryItem
          iconButtonLabel='Expand'
          iconButtonProps={{iconStyle: 'regular'}}
          id={`${category}_expand`}
          onMenuItemClick={() => {
            if (makeItemHref !== undefined) {
              router.push(makeItemHref(category, 'All'));
            }
          }}
          selected={activePath.length === 1 && isCategoryActive}
          text={getOverriddenDomainString(category) ?? category}
        >
          {renderItems(category, items)}
        </ExpandableMenuListItem>
      </li>
    );
  };

  function renderItems(category: string, items: List<string>) {
    const WrapperComponent = makeItemHref !== undefined ? Link : 'li';

    return (
      <ul className='cl-sidebar-menu__items-list'>
        {items.map((item = '') => (
          <MenuListItem
            as={WrapperComponent}
            indentLevel='1'
            key={`${category}-${item}`}
            onMenuItemClick={() => onItemClick(category, item)}
            regularTextProps={{bold: false}}
            selected={isItemActive(category, item)}
            text={getOverriddenGroupString(category, item) ?? item}
            to={makeItemHref !== undefined ? makeItemHref(category, item) : null}
          />
        ))}
      </ul>
    );
  }

  const triggerElement = (
    <Card paddingLevel={2} className='cl-sidebar-menu__dropdown'>
      <div className='cl-sidebar-menu__dropdown-text'>
        <Text size='s' color='tertiary'>
          {group}
        </Text>
        <Text>{subgroup}</Text>
      </div>
      <Icon icon='caret-down' />
    </Card>
  );

  return (
    <MobileDropdown fillWrapper className='cl-sidebar-menu' trigger={triggerElement}>
      <div className='cl-sidebar-menu__list u-gap_space-x1'>
        {renderAdditionalSidebarMenuItems()}
        <ul className='cl-sidebar-menu__list__categories u-padding-inline_space-x2 u-padding-block-start_space-x1 u-padding-block-end_space-x3'>
          {menuItems.map((value = List(), key = '') => renderCategories(key, value)).valueSeq()}
        </ul>
      </div>
    </MobileDropdown>
  );
}

export default withRouter(SidebarMenu);
